import styled, { createGlobalStyle } from 'styled-components';

/**
 * Rapportsidans egen formgivning.
 *
 * Sidan har två uppgifter: läsas på skärm och skrivas ut till PDF ur webbläsaren. Därför bor
 * tokens och utskriftsreglerna på samma ställe — driver de isär blir skärmen och pappret två
 * olika rapporter, och det är pappret som skickas vidare.
 *
 * Färgerna är samma validerade serie som dashboarden använder. Ordningen är vald för att
 * intilliggande par ska gå att skilja åt även vid färgblindhet, så byt inte plats på dem.
 */
export const ReportGlobalStyle = createGlobalStyle`
  .zg-report {
    --ground:   #f7f7f5;
    --panel:    #ffffff;
    --panel-2:  #f2f3f0;
    --ink:      #14181c;
    --ink-2:    #4d545c;
    --ink-3:    #7d858e;
    --rule:     #dfe2de;
    --rule-2:   #c3c8c2;
    --accent:   #1c3f6e;
    --accent-soft: #e9eef5;
    --good:     #1f7a4d;
    --warn:     #8a5a00;
    --warn-bg:  #faf2de;
    --stop:     #a3352c;
    --stop-bg:  #fbe9e7;
    --track:    #e8eae6;

    --s1: #2a78d6;
    --s2: #eb6834;
    --s3: #1baf7a;
    --wait: #bad4f2;

    --o1: #c3daf6; --o2: #86b6ef; --o3: #5598e7; --o4: #2a78d6; --o5: #1c5cab;
  }

  @media print {
    /* Utskriften ska bära rapporten, inte appen runt omkring den. Navigationen är en div och
       inte ett <nav>, så den måste märkas ut — se NavBarContainer. */
    .zg-noprint, .zg-report-noprint { display: none !important; }

    html, body { background: #fff !important; }

    @page { size: A4; margin: 13mm 12mm; }

    .zg-report { background: #fff !important; padding: 0 !important; }
    .zg-report * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }

    /* En panel som delas mitt itu är oläsbar — hellre luft före sidbrytningen. */
    .zg-panel { break-inside: avoid; page-break-inside: avoid; }
    .zg-pagebreak { break-before: page; page-break-before: always; }
  }
`;

export const ReportRoot = styled.div.attrs({ className: 'zg-report' })`
  background: var(--ground);
  color: var(--ink);
  font-family: 'Barlow', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 14px;
  line-height: 1.45;
  padding: 0 20px 64px;
  min-height: 100%;
`;

export const Sheet = styled.div`
  max-width: 1180px;
  margin: 0 auto;
`;

export const Mono = styled.span`
  font-family: ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace;
  font-variant-numeric: tabular-nums;
`;

/* ---------------- huvud ---------------- */

export const Masthead = styled.header`
  border-top: 3px solid var(--accent);
  padding-top: 16px;
  margin-top: 8px;
`;

export const Kicker = styled.div`
  font-family: ui-monospace, monospace;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-3);
`;

export const Title = styled.h1`
  font-size: clamp(22px, 3vw, 30px);
  font-weight: 700;
  letter-spacing: -0.022em;
  margin: 4px 0 0;
`;

export const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 18px;
  margin-top: 8px;
  font-size: 13px;
  color: var(--ink-2);

  b {
    color: var(--ink);
    font-weight: 600;
  }
`;

export const Toolbar = styled.div.attrs({ className: 'zg-report-noprint' })`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin: 18px 0 0;
`;

export const Segmented = styled.div`
  display: flex;
  border: 1px solid var(--rule-2);

  button {
    font-family: ui-monospace, monospace;
    font-size: 12px;
    font-weight: 500;
    padding: 7px 13px;
    border: none;
    border-right: 1px solid var(--rule-2);
    background: var(--panel);
    color: var(--ink-2);
    cursor: pointer;
    white-space: nowrap;

    &:last-child {
      border-right: none;
    }
    &:hover {
      background: var(--panel-2);
      color: var(--ink);
    }
    &[aria-pressed='true'] {
      background: var(--accent);
      color: #fff;
    }
    &:disabled {
      color: var(--ink-3);
      cursor: default;
      opacity: 0.45;
    }
    &:disabled:hover {
      background: var(--panel);
      color: var(--ink-3);
    }
    &:focus-visible {
      outline: 2px solid var(--s2);
      outline-offset: -2px;
    }
  }
`;

/* ---------------- rubriksiffror ---------------- */

export const Headlines = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  background: var(--rule);
  border: 1px solid var(--rule);
  margin-top: 20px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

export const Headline = styled.div`
  background: var(--panel);
  padding: 14px 16px 15px;

  .hl-label {
    font-size: 12.5px;
    color: var(--ink-2);
    font-weight: 500;
  }
  .hl-value {
    font-family: ui-monospace, monospace;
    font-variant-numeric: tabular-nums;
    font-size: 29px;
    font-weight: 600;
    letter-spacing: -0.035em;
    margin-top: 4px;
    line-height: 1;
  }
  .hl-value small {
    font-size: 15px;
    font-weight: 500;
    color: var(--ink-3);
    letter-spacing: 0;
  }
  .hl-sub {
    font-size: 12px;
    color: var(--ink-3);
    margin-top: 6px;
  }
  .hl-trend {
    margin-top: 7px;
    display: flex;
    align-items: baseline;
    gap: 6px;
    font-size: 11px;
    color: var(--ink-3);
  }
`;

/**
 * Skillnaden mot jämförelseperioden, som en liten etikett.
 *
 * Riktningen och omdömet är två olika saker och hålls isär: pilen säger vilket håll talet gick,
 * $tone säger om det var bra. Kortare ledtid är en nedåtpil i grönt, färre levererade bilar en
 * nedåtpil i rött — samma pil, olika omdöme. Utan den uppdelningen läser ledningsgruppen av
 * färgen och drar fel slutsats på hälften av talen.
 */
export const Delta = styled.span<{ $tone: 'good' | 'bad' | 'flat' }>`
  display: inline-flex;
  align-items: baseline;
  gap: 3px;
  font-family: ui-monospace, monospace;
  font-variant-numeric: tabular-nums;
  font-size: 11.5px;
  font-weight: 600;
  padding: 1px 5px 2px;
  border-radius: 3px;
  letter-spacing: -0.01em;
  white-space: nowrap;

  color: ${(p) =>
    p.$tone === 'good' ? 'var(--good)' : p.$tone === 'bad' ? 'var(--stop)' : 'var(--ink-3)'};
  background: ${(p) =>
    p.$tone === 'good' ? '#e8f3ed' : p.$tone === 'bad' ? 'var(--stop-bg)' : 'var(--panel-2)'};
`;

/**
 * Ramen runt datumväljaren.
 *
 * Väljaren är appens delade komponent och bär med sig sin egen typografi, så den behöver
 * dras ner i storlek för att stå bredvid de segmenterade knapparna utan att dominera raden.
 * Den försvinner i utskriften: på pappret är perioden redan tryckt i huvudet, och ett
 * inmatningsfält i en rapport som mailas vidare är bara förvirrande.
 */
export const RangePick = styled.div`
  display: flex;
  align-items: center;

  /* DateRangeInput renderar en <button>, inte en <input> — react-datepicker ritar sitt eget
     fält bara när den saknar customInput. En regel på input hade träffat ingenting. */
  button {
    font-family: ui-monospace, monospace;
    font-size: 12px;
    padding: 6px 10px;
    height: 32px;
    min-width: 190px;
  }

  @media print {
    display: none;
  }
`;

export const TrendTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;

  th,
  td {
    padding: 6px 9px 6px 0;
    border-bottom: 1px solid var(--rule);
    text-align: left;
    vertical-align: baseline;
  }
  th {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--ink-3);
    border-bottom: 1px solid var(--rule-2);
  }
  td.n,
  th.n {
    text-align: right;
    font-family: ui-monospace, monospace;
    font-variant-numeric: tabular-nums;
    padding-left: 9px;
  }
  td.name {
    color: var(--ink);
  }
  td.name small {
    display: block;
    color: var(--ink-3);
    font-size: 11px;
    margin-top: 1px;
  }
  tr:last-child td {
    border-bottom: none;
  }
`;

/* ---------------- månad för månad ---------------- */

/**
 * Månadstabellen. Tätare än trendtabellen: tolv månader och året ska rymmas på ett stående A4
 * utan att tabellen bryts eller skalas ned till oläslighet.
 */
export const MonthTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;

  th,
  td {
    padding: 5px 4px;
    border-bottom: 1px solid var(--rule);
    text-align: left;
    vertical-align: baseline;
    white-space: nowrap;
  }
  th {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--ink-3);
    border-bottom: 1px solid var(--rule-2);
  }
  td.n,
  th.n {
    text-align: right;
    font-family: ui-monospace, monospace;
    font-variant-numeric: tabular-nums;
  }
  td.name {
    color: var(--ink);
    padding-right: 10px;
  }
  td.name small {
    display: block;
    color: var(--ink-3);
    font-size: 11px;
    margin-top: 1px;
  }
  .year {
    font-weight: 600;
    color: var(--ink);
    border-left: 1px solid var(--rule-2);
    padding-left: 8px;
  }
  tr:last-child td {
    border-bottom: none;
  }

  @media print {
    font-size: 10.5px;
    th,
    td {
      padding: 4px 3px;
    }
  }
`;

/** En månadscell, tonad efter hur månaden står sig mot samma månad i fjol. */
export const MonthCell = styled.td<{ $tone: 'good' | 'bad' | 'flat' }>`
  background: ${(p) =>
    p.$tone === 'good' ? '#e8f3ed' : p.$tone === 'bad' ? 'var(--stop-bg)' : 'transparent'};
  color: ${(p) =>
    p.$tone === 'good' ? 'var(--good)' : p.$tone === 'bad' ? 'var(--stop)' : 'var(--ink)'};
`;

/** P1 per månad: ett stapelpar per månad, i år och i fjol. */
export const MonthBars = styled.div`
  display: flex;
  gap: 6px;
  align-items: flex-end;
  margin-top: 6px;

  .month {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .value {
    font-family: ui-monospace, monospace;
    font-variant-numeric: tabular-nums;
    font-size: 11px;
    color: var(--ink-2);
    margin-bottom: 3px;
  }
  .pair {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: flex-end;
    gap: 3px;
    border-bottom: 1px solid var(--rule-2);
  }
  .before,
  .now {
    width: 38%;
    max-width: 26px;
  }
  .before {
    background: var(--rule-2);
  }
  .now {
    background: var(--s1);
  }
  .now.partial {
    opacity: 0.5;
  }
  .label {
    font-family: ui-monospace, monospace;
    font-size: 11px;
    color: var(--ink-3);
    margin-top: 4px;
  }
`;

/** Månadsstaplarna under en rubrikbricka. */
export const Spark = styled.svg`
  display: block;
  width: 100%;
  height: 24px;
  margin-top: 8px;
`;

export const Finding = styled.div`
  margin-top: 16px;
  background: var(--panel-2);
  border-left: 3px solid var(--s2);
  padding: 12px 15px;

  .f-title {
    font-family: ui-monospace, monospace;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--s2);
  }
  p {
    margin: 5px 0 0;
    font-size: 14.5px;
    line-height: 1.45;
    color: var(--ink-2);
    max-width: 92ch;
  }
  strong {
    color: var(--ink);
    font-weight: 600;
  }
`;

/* ---------------- paneler ---------------- */

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 16px;
  margin-top: 16px;
  align-items: start;

  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
  }
`;

export const Panel = styled.section.attrs({ className: 'zg-panel' })<{
  $span?: number;
  $flag?: boolean;
}>`
  grid-column: span ${({ $span }) => $span ?? 12};
  background: var(--panel);
  border: 1px solid var(--rule);
  ${({ $flag }) => ($flag ? 'border-top: 2px solid var(--s2);' : '')}
  padding: 16px 18px 18px;
  min-width: 0;

  @media (max-width: 1000px) {
    grid-column: span 1;
  }
`;

export const PanelHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 9px;
  flex-wrap: wrap;
  padding-bottom: 10px;
  margin-bottom: 14px;
  border-bottom: 1px solid var(--rule);

  h2 {
    font-size: 16px;
    font-weight: 600;
    letter-spacing: -0.012em;
    margin: 0;
  }
  .tag {
    font-family: ui-monospace, monospace;
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.07em;
    color: var(--ink-3);
    border: 1px solid var(--rule-2);
    padding: 1px 5px;
    align-self: center;
  }
  .spacer {
    flex: 1 1 auto;
  }
  .unit {
    font-family: ui-monospace, monospace;
    font-size: 11px;
    color: var(--ink-3);
    align-self: center;
  }
`;

export const Note = styled.p`
  font-size: 13.5px;
  line-height: 1.45;
  color: var(--ink-2);
  margin: 14px 0 0;
  max-width: 80ch;

  strong {
    color: var(--ink);
    font-weight: 600;
  }
`;

/* ---------------- staplar ---------------- */

export const Bars = styled.div`
  display: grid;
  gap: 8px;
`;

export const BarRow = styled.div<{ $total?: boolean; $withDelta?: boolean }>`
  display: grid;
  grid-template-columns: ${({ $withDelta }) =>
    $withDelta ? '150px 1fr 74px 96px' : '150px 1fr 90px'};
  gap: 12px;
  align-items: center;
  ${({ $total }) =>
    $total ? 'border-top: 1px solid var(--rule-2); padding-top: 8px;' : ''}

  .b-name {
    font-size: 13.5px;
    color: ${({ $total }) => ($total ? 'var(--ink)' : 'var(--ink-2)')};
    font-weight: ${({ $total }) => ($total ? 600 : 400)};
  }
  .b-track {
    background: var(--track);
    height: 15px;
  }
  .b-fill {
    height: 15px;
    background: var(--s1);
  }
  .b-val {
    font-family: ui-monospace, monospace;
    font-variant-numeric: tabular-nums;
    font-size: 13.5px;
    text-align: right;
    font-weight: ${({ $total }) => ($total ? 600 : 500)};
  }
  .b-delta {
    display: flex;
    justify-content: flex-end;
  }

  @media (max-width: 560px) {
    /* Stapeln får vika ner sig först. Talet och dess förändring hör ihop och ska stå kvar
       bredvid varandra — ett delta utan sitt tal är inte läsbart. */
    grid-template-columns: ${({ $withDelta }) =>
      $withDelta ? '110px 1fr 60px 92px' : '110px 1fr 74px'};
    gap: 8px;
  }
`;

/* ---------------- ledtid ---------------- */

export const LeadRow = styled.div<{ $total?: boolean }>`
  display: grid;
  grid-template-columns: 150px 1fr 90px;
  gap: 12px;
  align-items: center;
  ${({ $total }) =>
    $total ? 'border-top: 1px solid var(--rule-2); padding-top: 8px;' : ''}

  .l-name {
    font-size: 13.5px;
    color: ${({ $total }) => ($total ? 'var(--ink)' : 'var(--ink-2)')};
    font-weight: ${({ $total }) => ($total ? 600 : 400)};
  }
  .l-name span {
    display: block;
    font-size: 11.5px;
    color: var(--ink-3);
    font-weight: 400;
  }
  .l-track {
    display: flex;
    gap: 2px;
    height: 18px;
    background: var(--track);
  }
  .l-drive {
    background: var(--s1);
  }
  .l-wait {
    background: var(--wait);
  }
  .l-val {
    font-family: ui-monospace, monospace;
    font-variant-numeric: tabular-nums;
    font-size: 13.5px;
    text-align: right;
    font-weight: ${({ $total }) => ($total ? 600 : 500)};
  }

  @media (max-width: 560px) {
    grid-template-columns: 110px 1fr 74px;
  }
`;

export const Axis = styled.div`
  display: grid;
  grid-template-columns: 150px 1fr 90px;
  gap: 12px;
  margin-top: 2px;

  .ticks {
    display: flex;
    justify-content: space-between;
    border-top: 1px solid var(--rule);
    padding-top: 3px;
    font-family: ui-monospace, monospace;
    font-size: 10.5px;
    color: var(--ink-3);
  }

  @media (max-width: 560px) {
    grid-template-columns: 110px 1fr 74px;
  }
`;

/* ---------------- 100 %-stapel och legend ---------------- */

export const Stack = styled.div`
  display: flex;
  height: 38px;
  gap: 2px;

  > div {
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: ui-monospace, monospace;
    font-size: 13px;
    font-weight: 600;
    color: #fff;
    min-width: 0;
    overflow: hidden;
  }
`;

export const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px 16px;
  margin-top: 10px;

  span.item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12.5px;
    color: var(--ink-2);
  }
  span.swatch {
    width: 11px;
    height: 11px;
    flex: none;
  }
`;

/* ---------------- tabeller ---------------- */

export const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  font-size: 13.5px;

  th {
    text-align: left;
    font-weight: 600;
    font-size: 10.5px;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: var(--ink-3);
    padding: 0 10px 7px 0;
    border-bottom: 1px solid var(--rule);
    white-space: nowrap;
  }
  td {
    padding: 7px 10px 7px 0;
    border-bottom: 1px solid var(--rule);
  }
  tbody tr:last-child td {
    border-bottom: none;
  }
  td.num,
  th.num {
    text-align: right;
    padding-right: 0;
    padding-left: 10px;
    font-family: ui-monospace, monospace;
    font-variant-numeric: tabular-nums;
  }
  tr.total td {
    font-weight: 600;
    border-top: 1px solid var(--rule-2);
    border-bottom: none;
  }
  tr.warn td {
    color: var(--warn);
  }
  /* Den utfällda listan hör ihop med raden ovanför, så linjen mellan dem tas bort och
     ramas i stället in av en enda linje under listan. */
  tr:has(+ tr.detail) td {
    border-bottom: none;
  }
  tr.detail td {
    padding: 0 0 6px;
    border-bottom: 1px solid var(--rule);
  }
`;

export const TableWrap = styled.div`
  overflow-x: auto;
`;

/* ---------------- utfällbara detaljlistor ---------------- */

/**
 * Orsaksnamnet som fäller ut listan. Formgiven som text och inte som en knapp: raden är
 * fortfarande en tabellrad att läsa, och en knappformad cell hade dragit blicken från talen.
 * Karetten är hela signalen om att det går att klicka.
 */
export const Disclosure = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  color: var(--accent);
  text-align: left;
  cursor: pointer;

  .caret {
    width: 8px;
    font-size: 9px;
    color: var(--ink-3);
  }

  &:hover {
    text-decoration: underline;
  }

  /* En orsak utan bilar i perioden går inte att fälla ut — då ska den inte se ut som en länk. */
  &:disabled {
    color: var(--ink);
    cursor: default;
  }
  &:disabled:hover {
    text-decoration: none;
  }
  &:disabled .caret {
    visibility: hidden;
  }
`;

export const DetailHead = styled.div`
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ink-3);
  padding: 2px 0 5px;
`;

/**
 * Bilarna bakom en orsak.
 *
 * Flera kolumner och ett tak på höjden, eftersom listan är lika lång som perioden är: en
 * rapportvecka ger tiotals rader, men "År 1 jan → nu" ger tusentals, och en panel som växer
 * till en skärmlängd gör resten av rapporten oläslig. Taket lyfts i utskrift — på papper finns
 * ingen scroll, och en kapad lista hade tystnat utan att säga att den var kapad.
 */
export const DetailList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0 0 2px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(175px, 1fr));
  gap: 1px 20px;
  max-height: 260px;
  overflow-y: auto;

  li {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    padding: 1px 0;
    font-size: 12.5px;
    font-family: ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace;
    font-variant-numeric: tabular-nums;
    color: var(--ink-2);
  }

  .date {
    color: var(--ink-3);
  }

  /* Ett ärende med flera avbokade etapper. Märks ut så att raden och talet går ihop. */
  .legs {
    color: var(--warn);
  }

  .missing {
    color: var(--ink-3);
    font-style: italic;
  }

  /* Regnumret länkar till ärendet. Understrykningen är diskret: listan ska läsas som en lista. */
  a.case {
    color: inherit;
    text-decoration: underline dotted var(--ink-3);
    text-underline-offset: 2px;

    &:hover {
      color: var(--accent);
      text-decoration-color: currentColor;
    }
  }

  @media print {
    max-height: none;
    overflow: visible;

    a.case {
      text-decoration: none;
    }
  }
`;

/** Bilarna bakom en orsak till ändrad bokningstid — raden bär från/till, så den behöver bredd. */
export const ShiftDetailList = styled(DetailList)`
  /* min() låter kolumnen krympa till panelens bredd i smala fönster i stället för att spilla över. */
  grid-template-columns: repeat(auto-fill, minmax(min(420px, 100%), 1fr));

  .type,
  .county {
    color: var(--ink-3);
    font-family: inherit;
  }

  .county {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

/** Nyckeltalen överst i en panel: tal, etikett och delta under varandra. */
export const Figures = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px 18px;
  margin: 2px 0 14px;

  > div {
    border-left: 2px solid var(--rule-2);
    padding-left: 10px;
    min-width: 0;
  }
  .value {
    font-size: 22px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    line-height: 1.15;
    color: var(--ink);
  }
  .label {
    font-size: 12px;
    color: var(--ink-2);
  }
`;

/* ---------------- luckor ---------------- */

export const Gaps = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0 26px;

  @media (max-width: 1000px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

export const Gap = styled.div`
  display: grid;
  grid-template-columns: 56px 1fr;
  gap: 10px;
  align-items: baseline;
  padding: 9px 0;
  border-bottom: 1px solid var(--rule);

  .g-num {
    font-family: ui-monospace, monospace;
    font-variant-numeric: tabular-nums;
    font-size: 16px;
    font-weight: 600;
    text-align: right;
  }
  .g-text {
    font-size: 13px;
    color: var(--ink-2);
    line-height: 1.38;
  }
  .g-chip {
    display: inline-block;
    margin-top: 4px;
    font-family: ui-monospace, monospace;
    font-size: 9.5px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 2px 6px;
  }
  .g-chip.stop {
    background: var(--stop-bg);
    color: var(--stop);
  }
  .g-chip.out {
    background: var(--warn-bg);
    color: var(--warn);
  }
  .g-chip.ctx {
    background: var(--panel-2);
    color: var(--ink-3);
  }
`;

/* ---------------- metod ---------------- */

export const Definitions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0 26px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }

  dl {
    margin: 0;
    padding: 8px 0;
    border-bottom: 1px solid var(--rule);
  }
  dt {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--ink);
  }
  dd {
    margin: 2px 0 0;
    font-size: 12.5px;
    color: var(--ink-2);
    line-height: 1.4;
  }
`;

export const ReportFooter = styled.div`
  margin-top: 28px;
  padding-top: 14px;
  border-top: 1px solid var(--rule);
  font-size: 12.5px;
  color: var(--ink-3);
  display: flex;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

export const StateBox = styled.div`
  padding: 40px 0;
  text-align: center;
  color: var(--ink-2);
`;

export const WarnBox = styled.div`
  background: var(--warn-bg);
  border-left: 3px solid var(--warn);
  padding: 11px 14px;
  font-size: 13.5px;
  color: var(--ink-2);
`;
